import React from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AlertInfo } from "../../../components/alert";
import { loadingAction } from "../../../redux/actions";
import { BASE_URL, EstadoContenido } from "../../../utils/constants";
import { Fetch, catchError } from "../../../utils/fetch";
import MediaUploader from "../../../utils/media-uploader";

const EMPTY_CONFIGURATION = {
  logoCabecera: null,
  logoFooter: null,
  loader: null,
  favicon: null,
};

const FIELDS = [
  {
    name: "logoCabecera",
    label: "Logo de cabecera",
    help: "Archivos admitidos: PNG o SVG.",
    accept: "image/png,image/svg+xml",
    allowedMimeTypes: ["image/png", "image/svg+xml"],
  },
  {
    name: "logoFooter",
    label: "Logo de pie de página",
    help: "Archivos admitidos: PNG o SVG.",
    accept: "image/png,image/svg+xml",
    allowedMimeTypes: ["image/png", "image/svg+xml"],
  },
  {
    name: "loader",
    label: "Logo animado de carga",
    help: "Archivos admitidos: WebM o MP4.",
    accept: "video/webm,video/mp4",
    allowedMimeTypes: ["video/webm", "video/mp4"],
  },
  {
    name: "favicon",
    label: "Favicon",
    help: "Archivos admitidos: PNG o ICO.",
    accept: "image/png,image/x-icon,image/vnd.microsoft.icon",
    allowedMimeTypes: ["image/png", "image/x-icon", "image/vnd.microsoft.icon"],
  },
];

const STATUS_LABELS = {
  [EstadoContenido.EnRevision]: "En revisión",
  [EstadoContenido.EnAprobacion]: "Por aprobar",
  [EstadoContenido.Publicado]: "Publicado",
};

const resolveMediaUrl = (media) => {
  if (!media || !media.url) return "";
  return media.url.startsWith("http") ? media.url : `${BASE_URL}${media.url}`;
};

export const ConfiguracionLogosPage = ({ estado, nuevo = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userType = useSelector((state) => state.user.data.role.type);
  const isLoading = useSelector((state) => state.loading);
  const [configuration, setConfiguration] = React.useState({
    ...EMPTY_CONFIGURATION,
    estado,
  });

  const isApprover = userType === "aprobador";
  const canEdit = ["creador", "aprobador"].includes(userType);
  const isReadOnly = estado === EstadoContenido.EnAprobacion || !canEdit;

  const loadConfiguration = React.useCallback(async () => {
    dispatch(loadingAction(true));
    try {
      const queryEstado = nuevo ? EstadoContenido.Publicado : estado;
      const endpoint = `configuracion-logos?estado=${queryEstado}`;
      const { data } = await Fetch(endpoint, "get");
      const content = data && data[0];
      setConfiguration({
        ...EMPTY_CONFIGURATION,
        ...(content || {}),
        ...(nuevo ? { id: undefined } : {}),
        estado,
      });
    } catch (error) {
      catchError(error);
    } finally {
      dispatch(loadingAction(false));
    }
  }, [dispatch, estado, nuevo]);

  React.useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const setMedia = (field, value) => {
    setConfiguration((current) => ({ ...current, [field]: value }));
  };

  const hasConfiguration = Boolean(configuration.id);
  const showForm = nuevo || hasConfiguration;

  const execute = async (
    endpoint,
    method,
    message,
    redirectTo,
    changes = {}
  ) => {
    dispatch(loadingAction(true));
    try {
      await Fetch(endpoint, method, { ...configuration, ...changes });
      AlertInfo(message);
      history.push(redirectTo);
    } catch (error) {
      catchError(error);
    } finally {
      dispatch(loadingAction(false));
    }
  };

  const renderActions = () => {
    if (!canEdit) return null;

    if (nuevo) {
      return (
        <Row className="mt-5">
          <Col className="col col-lg-2">
            <Button
              type="button"
              disabled={isLoading}
              className="btn btn-success btn-icon mt-2"
              onClick={() =>
                execute(
                  "configuracion-logos/estado/borrador",
                  "post",
                  "Logo creado con éxito.",
                  "/configuracion/logos/borrador"
                )
              }
            >
              <i className="fa fa-floppy-o " />
              Guardar
            </Button>
          </Col>
        </Row>
      );
    }

    if (estado === EstadoContenido.Publicado) {
      return (
        <Row className="mt-5">
          <Col className="col col-lg-2">
            <Button
              type="button"
              disabled={isLoading}
              className="btn btn-success btn-icon"
              onClick={() =>
                execute(
                  "configuracion-logos/estado/borrador",
                  "post",
                  "Configuración enviada a revisión.",
                  "/configuracion/logos/borrador"
                )
              }
            >
              <i className="fa fa-floppy-o " />
              Guardar
            </Button>
          </Col>
        </Row>
      );
    }

    if (estado === EstadoContenido.EnRevision) {
      return (
        <Row className="mt-5">
          <Col className="col col-lg-2">
            <Button
              type="button"
              disabled={isLoading}
              className="btn btn-success btn-icon no-hover"
              onClick={() =>
                execute(
                  `configuracion-logos/${configuration.id}`,
                  "put",
                  "Configuración guardada en revisión.",
                  "/configuracion/logos/borrador"
                )
              }
            >
              <i className="fa fa-floppy-o " />
              Guardar
            </Button>
          </Col>
          <Col className="col col-lg-3">
            <Button
              type="button"
              disabled={isLoading || !hasConfiguration}
              className="btn btn-info btn-icon no-hover2"
              onClick={() =>
                execute(
                  "configuracion-logos/estado/aprobar",
                  "post",
                  "Configuración enviada para aprobación.",
                  "/configuracion/logos/aprobar"
                )
              }
            >
              <i className="fa fa-share " />
              Enviar aprobar
            </Button>
          </Col>
        </Row>
      );
    }

    if (estado === EstadoContenido.EnAprobacion && isApprover) {
      return (
        <Row className="mt-5">
          <Col className="col col-lg-2">
            <Button
              type="button"
              disabled={isLoading}
              className="btn btn-success btn-icon no-hover"
              onClick={() =>
                execute(
                  "configuracion-logos/estado/publicar",
                  "post",
                  "Configuración publicada correctamente.",
                  "/configuracion/logos/publicado"
                )
              }
            >
              <i className="fa fa-floppy-o " />
              Publicar
            </Button>
          </Col>
          <Col className="col col-lg-3">
            <Button
              type="button"
              disabled={isLoading}
              className="btn btn-primary btn-icon no-hover3"
              onClick={() =>
                execute(
                  `configuracion-logos/${configuration.id}`,
                  "put",
                  "Configuración devuelta a revisión.",
                  "/configuracion/logos/borrador",
                  { estado: EstadoContenido.EnRevision }
                )
              }
            >
              <i className="fa fa-undo " />
              Por revisar
            </Button>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <Row>
      <Col>
        <Card>
          <Card.Header>
            <Row>
              <Col>
                <h3>{nuevo ? "Nuevo logo" : "Logos"}</h3>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            {!showForm && (
              <Alert variant="warning">
                No hay una configuración en estado {STATUS_LABELS[estado].toLowerCase()}.
              </Alert>
            )}

            {showForm && (
              <>
                <Row>
                  {FIELDS.map((field) => {
                    const media = configuration[field.name];
                    return (
                      <Col sm={12} md={6} key={field.name}>
                        <Form.Group className="mb-5">
                          <label className="font-weight-bold" style={{ fontSize: "1.1rem" }}>
                            {field.label}
                          </label>
                          {media && (
                            <>
                              <div>
                                {field.name === "loader" ? (
                                  <video
                                    src={resolveMediaUrl(media)}
                                    controls
                                    muted
                                    playsInline
                                    className="img-fluid w-50"
                                  />
                                ) : (
                                  <img
                                    src={resolveMediaUrl(media)}
                                    alt={field.label}
                                    className="img-fluid w-50"
                                  />
                                )}
                              </div>
                              <div className="text-muted mt-1">{media.name}</div>
                            </>
                          )}
                          {!isReadOnly && media && (
                            <Button
                              type="button"
                              className="btn btn-danger btn-icon mt-2 mb-2"
                              onClick={() => setMedia(field.name, null)}
                            >
                              <i className="fa fa-trash-o " />
                              Eliminar
                            </Button>
                          )}
                          {!isReadOnly && !media && (
                            <>
                              <div>
                                <label>Seleccionar archivo</label>
                              </div>
                              <MediaUploader
                                setFieldValue={setMedia}
                                field={field.name}
                                tipoMedia={field.name === "loader" ? "video" : "logo"}
                                accept={field.accept}
                                allowedMimeTypes={field.allowedMimeTypes}
                                value={media ? 100 : 0}
                                key={`${field.name}-${media ? media.id : "empty"}`}
                              />
                            </>
                          )}
                          <div className="text-muted mt-2">{field.help}</div>
                        </Form.Group>
                      </Col>
                    );
                  })}
                </Row>
                {renderActions()}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ConfiguracionLogosPage;
